import React from 'react';
import { Button, Checkbox, Flex, Select, Text, TextField, useToast, TagBadge } from '@chatsdet/ui';
import type { ApiQueryParamDef } from '@chatsdet/types';
import { Plus, Trash, FloppyDisk } from '@phosphor-icons/react';
import { useUpdateApiRequest } from '../../../features/api-catalog/api';

const PARAM_TYPES: Array<{ id: string; label: string }> = [
  { id: 'string', label: 'string' },
  { id: 'number', label: 'number' },
  { id: 'boolean', label: 'boolean' },
  { id: 'array', label: 'array' },
];

type Props = {
  requestId: string;
  schema: ApiQueryParamDef[] | null;
};

export function QueryParamSchemaEditor({ requestId, schema }: Props) {
  const { toast } = useToast();
  const updateMutation = useUpdateApiRequest();
  const [params, setParams] = React.useState<ApiQueryParamDef[]>(schema ?? []);
  const [isDirty, setIsDirty] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    setParams(schema ?? []);
    setIsDirty(false);
    setEditingIndex(null);
  }, [requestId, schema]);

  const handleAdd = React.useCallback(() => {
    setParams((prev) => {
      const next = [
        ...prev,
        { name: '', type: 'string' as const, required: false, description: '', defaultValue: '' },
      ];
      setEditingIndex(next.length - 1);
      return next;
    });
    setIsDirty(true);
  }, []);

  const handleRemove = React.useCallback(
    (index: number) => {
      setParams((prev) => prev.filter((_, i) => i !== index));
      if (editingIndex === index) setEditingIndex(null);
      else if (editingIndex !== null && editingIndex > index)
        setEditingIndex(editingIndex - 1);
      setIsDirty(true);
    },
    [editingIndex]
  );

  const handleChange = React.useCallback(
    (index: number, field: keyof ApiQueryParamDef, value: string | boolean) => {
      setParams((prev) =>
        prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
      );
      setIsDirty(true);
    },
    []
  );

  const handleSave = React.useCallback(() => {
    const cleaned = params
      .filter((p) => p.name.trim())
      .map((p) => ({
        name: p.name.trim(),
        type: p.type as ApiQueryParamDef['type'],
        required: p.required,
        ...(p.description?.trim() ? { description: p.description.trim() } : {}),
        ...(p.defaultValue?.trim() ? { defaultValue: p.defaultValue.trim() } : {}),
      }));

    updateMutation.mutate(
      { requestId, queryParamSchema: cleaned.length > 0 ? cleaned : null },
      {
        onSuccess: () => {
          toast.success('Parameter schema saved');
          setIsDirty(false);
          setEditingIndex(null);
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to save');
        },
      }
    );
  }, [params, requestId, updateMutation, toast]);

  const handleRowClick = React.useCallback((index: number) => {
    setEditingIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleEditKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingIndex(null);
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        setEditingIndex(null);
      }
    },
    []
  );

  return (
    <div className="space-y-1">
      <Flex align="center" justify="between" className="px-3 py-1">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Query Parameters
        </span>
        <Flex gap="xs">
          {isDirty && (
            <Button
              variant="primary"
              size="sm"
              onPress={handleSave}
              isDisabled={updateMutation.isPending}
              iconLeft={<FloppyDisk size={14} />}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onPress={handleAdd} iconLeft={<Plus size={14} />}>
            Add
          </Button>
        </Flex>
      </Flex>

      {params.length === 0 ? (
        <Text size="xs" className="text-muted-foreground px-3 py-1">
          No query parameters defined.
        </Text>
      ) : (
        <div className="space-y-0.5">
          {params.map((param, index) =>
            editingIndex === index ? (
              <div
                key={index}
                className="px-3 py-2 bg-primary/5 ring-1 ring-primary/30 rounded space-y-2"
                onKeyDown={handleEditKeyDown}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <TextField
                      aria-label={`Parameter ${index + 1} name`}
                      value={param.name}
                      onChange={(val) => handleChange(index, 'name', val)}
                      placeholder="name"
                      className="font-mono"
                      autoFocus
                    />
                  </div>
                  <div className="w-[100px] shrink-0">
                    <Select
                      aria-label={`Parameter ${index + 1} type`}
                      items={PARAM_TYPES}
                      selectedKey={param.type}
                      onSelectionChange={(key) => handleChange(index, 'type', key as string)}
                    />
                  </div>
                  <Flex align="center" gap="xs" className="shrink-0">
                    <Checkbox
                      aria-label="Required"
                      isSelected={param.required}
                      onChange={(val) => handleChange(index, 'required', val)}
                    />
                    <Text size="xs" className="text-muted-foreground">
                      Required
                    </Text>
                  </Flex>
                  <Button
                    variant="icon"
                    iconOnly
                    size="sm"
                    onPress={() => handleRemove(index)}
                    tooltip="Remove"
                    className="shrink-0"
                  >
                    <Trash size={14} />
                  </Button>
                </div>
                <TextField
                  aria-label={`Parameter ${index + 1} description`}
                  value={param.description ?? ''}
                  onChange={(val) => handleChange(index, 'description', val)}
                  placeholder="Description (optional)"
                />
              </div>
            ) : (
              <div
                key={index}
                className="group flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-muted/60 transition-colors rounded"
                onClick={() => handleRowClick(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(index);
                  }
                }}
              >
                <span className="font-mono font-medium text-foreground">{param.name || '(unnamed)'}</span>
                <TagBadge name={param.type} color="gray" hideDot size="sm" className="text-[10px] shrink-0" />
                {param.required && (
                  <TagBadge name="required" color="red" hideDot size="sm" className="text-[10px] shrink-0" />
                )}
                {param.description && (
                  <span className="text-muted-foreground truncate flex-1 min-w-0" title={param.description}>
                    {param.description}
                  </span>
                )}
                {!param.description && <span className="flex-1" />}
                <button
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground/50 hover:text-destructive transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                  aria-label="Remove parameter"
                >
                  <Trash size={14} />
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
